import { useEffect, useCallback } from "react";
import { useAuth, hasAuthParams } from "react-oidc-context";
import { useNavigate, useLocation } from "react-router";
import { AuthorizationLevel } from "./types.js";
import { checkAuthQueryParams, getRedirectUrls, getPolicies } from "./utils.js";
function useAutoLoginLogout() {
  const auth = useAuth();
  const { error, isAuthenticated, isLoading, activeNavigator, signinRedirect, signinSilent } = auth;
  const navigate = useNavigate();
  const location2 = useLocation();
  useEffect(() => {
    loginWithRedirect();
  }, [isAuthenticated, activeNavigator, isLoading, signinRedirect]);
  useEffect(() => {
    if (error?.message?.length && !isAuthenticated && !isLoading && checkAuthQueryParams(location2.search)) {
      const { relativeUrl } = getRedirectUrls();
      navigate(relativeUrl, { replace: true });
      window.location.reload();
    }
  }, [error?.message, isAuthenticated, isLoading]);
  useEffect(() => {
    return (() => {
      return auth.events.addAccessTokenExpired(async () => {
        const { absoluteUrl } = getRedirectUrls();
        if (!await signinSilent()) {
          await signinRedirect({ redirect_uri: absoluteUrl });
        }
      });
    })();
  }, [auth.events, signinSilent, signinRedirect]);
  async function loginWithRedirect(forceLogin = false) {
    let signInInitiated = false;
    try {
      if ((!isAuthenticated || forceLogin) && !hasAuthParams() && !activeNavigator && !isLoading) {
        await signinRedirect();
        signInInitiated = true;
      }
    } catch {
    }
    return signInInitiated;
  }
  return { ...auth };
}
function useLogout() {
  const { signoutRedirect } = useAuth();
  async function logout() {
    try {
      await signoutRedirect();
    } catch {
    }
  }
  return { logout };
}
function useAuthUrl(origin) {
  const { search } = useLocation();
  const hasAuthQueryParams = checkAuthQueryParams(search);
  const { relativeUrl, absoluteUrl } = getRedirectUrls(origin);
  return { hasAuthQueryParams, relativeRedirectUrl: relativeUrl, absoluteRedirectUrl: absoluteUrl };
}
function useAuthUser() {
  const auth = useAuth();
  const user = auth?.user?.profile ?? { policies: [] };
  return { ...user, policies: (typeof user?.policies === "string" ? [user?.policies] : user?.policies) ?? [] };
}
function usePolicies() {
  const auth = useAuth();
  return getPolicies(auth?.user) ?? [];
}
function useAccount() {
  const { email, name, preferred_username } = useAuthUser();
  const { logout } = useLogout();
  return { email, name, preferred_username, handleLogout: logout };
}
function useOtpGuard() {
  const { user, signinRedirect } = useAuth();
  const verifyOtp = useCallback(() => {
    const acr = user?.profile?.acr;
    const now = Math.floor(Date.now() / 1e3);
    const otpTimeoutInSeconds = 3600;
    const isWithinOtpTimeout = now - (user?.profile?.auth_time ?? 0) < otpTimeoutInSeconds;
    const isOtpAuthenticated = Array.isArray(acr) ? acr.includes(AuthorizationLevel.TwoFactor) : acr === AuthorizationLevel.TwoFactor;
    if (!(isOtpAuthenticated && isWithinOtpTimeout)) {
      void signinRedirect({ acr_values: AuthorizationLevel.TwoFactor, redirect_uri: location.href });
      return false;
    }
    return true;
  }, [user, signinRedirect]);
  return { verifyOtp };
}
export {
  useAccount,
  useAuthUrl,
  useAuthUser,
  useAutoLoginLogout,
  useLogout,
  useOtpGuard,
  usePolicies
};
//# sourceMappingURL=hooks.js.map
